import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useParams, Link, useNavigate } from 'react-router-dom';
import html2canvas from 'html2canvas'; // Imported for PDF generation
import jsPDF from 'jspdf'; // Imported for PDF generation
import { trackOnboardingEvent } from '../Onboarding/utils/trackOnboarding';
// NOTE: DocumentUpload component is assumed to handle file uploads outside this component
import {
   BiInfoCircle
} from 'react-icons/bi';

import DocumentUpload from './DocumentUpload';

import {
  BiBriefcase,
  BiFile,
  BiArrowBack,
  BiEdit,
  BiDownload,
  BiTime,
  BiUser,
  BiBullseye,
} from 'react-icons/bi';

// Assuming API_BASE_URL is defined in this utility file
import { API_BASE_URL } from '../utils/urls.js';
import FieldUpdateSection from "./FieldUpdateSection";
import CaseMessaging from "./CaseMessaging";
import axiosClient from '../utils/apiClient.js';


// --- Reusable Info Box Component ---
const DetailBox = ({ title, icon, children, className = '' }) => (
  <div
    className={`bg-white border border-gray-300 rounded-lg p-5 space-y-3 mt-6 ${className}`}
  >
    <h3 className="text-lg font-extrabold uppercase text-gray-800 border-b-2 border-gray-300 pb-2 mb-3 flex items-center space-x-2">
      {icon}
      <span>{title}</span>
    </h3>
    {children}
  </div>
);

// --- Detail Item Component ---
const DetailItem = ({ label, value }) => {
  // Handles null, undefined, and empty string/array gracefully
  const displayValue = Array.isArray(value)
    ? value.length > 0
      ? value.join(', ')
      : null
    : value;

  if (displayValue === null || displayValue === undefined || displayValue === '')
    return null;

  // Also check if the array is just a comma-separated string of empty parts from the backend join logic
  if (typeof displayValue === 'string' && displayValue.trim() === '') return null;

  return (
    <p className="text-sm text-gray-800 mb-2">
      <span className="font-bold text-gray-600 mr-2 min-w-[120px] inline-block">
        {label}:
      </span>
      <span className="font-normal text-gray-900 whitespace-pre-wrap">
        {displayValue}
      </span>
    </p>
  );
};

// --- Subject File Display Component ---
// This uses a different download endpoint for subject files, based on the backend code's structure.
const SubjectFileItem = ({ file }) => (
  // NOTE: Adding 'print:hidden' to hide download links in the PDF document
  <div className="flex justify-between items-center text-xs text-gray-700 bg-gray-100 p-2 rounded-md border border-gray-200 print:hidden">
    <div className="flex flex-col w-full">
      <span className="font-semibold text-gray-900">
        {file.title || 'Untitled File'}
      </span>
      <span className="text-gray-500 truncate">{file.file_name}</span>
    </div>
    <a
      href={`${API_BASE_URL}/files/download?path=${file.file_path}`}
      target="_blank"
      rel="noopener noreferrer"
      className="ml-3 text-blue-600 hover:text-blue-900 font-semibold flex items-center space-x-1 flex-shrink-0"
    >
      <BiDownload className="w-4 h-4" />
      <span>Download</span>
    </a>
  </div>
);

// --- Main Component ---
const CaseDetailsPi = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [caseData, setCaseData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Ref for the section to be converted to PDF
  const printRef = useRef(null);

  const user = JSON.parse(localStorage.getItem('user'));
  const planType = user?.organization?.planType || 'N/A';
  const roleName = user?.roleName;
  const canEdit = roleName === 'admin' || roleName === 'case_manager';
  const canUpload =
    roleName === 'admin' || roleName === 'case_manager' || roleName === 'field_agent';

  // --- PDF Intake Document Download Feature ---
  const handleDownloadPdf = async () => {
    const element = printRef.current;
    if (!element) {
      alert('Content not found for PDF generation.');
      return;
    }

    try {
      // Use html2canvas to capture the content as an image
      const canvas = await html2canvas(element, {
        scale: 2, // Increase scale for better resolution
        useCORS: true, // Handle images from external sources if any
        logging: true,
      });

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
      });

      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgProps = pdf.getImageProperties(imgData);
      const imgHeight = (imgProps.height * pdfWidth) / imgProps.width;

      let position = 0;

      // Check if the content is longer than one page
      if (imgHeight > pdfHeight) {
        let heightLeft = imgHeight;
        while (heightLeft >= 0) {
          position = heightLeft - imgHeight;

          // Add a new page if it's not the first one
          if (heightLeft !== imgHeight) {
            pdf.addPage();
          }

          // Add the image, slicing the visible part
          pdf.addImage(imgData, 'PNG', 0, position, pdfWidth, imgHeight);
          
          heightLeft -= pdfHeight;
        }
      } else {
        // Content fits on a single page
        pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, imgHeight);
      }

      // Final step: Download the PDF
      pdf.save(`Case_Intake_Detail_${caseData.case_number || id}.pdf`);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Failed to generate PDF document.');
    }
  };
  // --- END PDF Feature ---


  // Consolidated API call: Only fetch the comprehensive case details
  const fetchData = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setError('Authentication token not found.');
      setLoading(false);
      return;
    }

    try {
      // ONLY ONE CALL NEEDED: The backend /pi-cases/:id endpoint returns all nested data
      const caseRes = await axiosClient.get(`/pi-cases/${id}`);

      setCaseData(caseRes.data);

    } catch (err) {
      console.error('Error fetching PI case details:', err);
      setError(
        err.response?.data?.message ||
          'Failed to load PI case details. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [id]);

  // Handler for documents/media download (uses the case download endpoint from original component)
  const handleDownload = async (documentId, fileName, type) => {
    const token = localStorage.getItem('token');
    try {
      const response = await axios.get(
        // NOTE: The backend code showed case_documents and case_media tables, but the
        // original frontend was calling /cases/download. Keeping this path.
        `${API_BASE_URL}/cases/download/${type}/${documentId}`,
        {
          responseType: 'blob',
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
    } catch (err) {
      console.error('Error downloading document:', err);
      alert('Failed to download document.');
    }
  };

  // Callback for when DocumentUpload succeeds (refreshes case data)
  const handleUploadSuccess = () => fetchData();

  // --- Loading/Error/Not Found States ---
  if (loading)
    return (
      <div className="p-10 text-center text-lg text-blue-600">
        Loading case details...
      </div>
    );

  if (error)
    return (
      <div className="p-10 text-center text-xl text-red-600">{error}</div>
    );

  if (!caseData)
    return (
      <div className="p-10 text-center text-lg text-gray-500">
        Case not found.
      </div>
    );

  // --- Destructure data directly from caseData ---
  const {
    client_info = {},
    custom_fields = {},
    documents = [],
    media = [],
    subjects = [],
    case_activity: activity = [], // Renaming for consistency with original component's state
    assigned_to_name,
  } = caseData;

  // Since the backend already provides 'assigned_to_name', we use it directly.
  const assignedAgentsDisplay = assigned_to_name || 'Unassigned';

  // --- NEW: Onboarding Guide Banner Component ---
  const OnboardingGuide = ({ step, title, description }) => (
      <div className="bg-indigo-50 border border-indigo-200 p-4 mt-8 mb-4 rounded-xl shadow-sm flex items-start space-x-4 print:hidden animate-fade-in">
          <div className="flex-shrink-0 bg-indigo-600 text-white font-black text-sm h-8 w-8 rounded-full flex items-center justify-center shadow-md">
              {step}
          </div>
          <div>
              <h4 className="text-indigo-900 font-bold text-sm sm:text-base">{title}</h4>
              <p className="text-indigo-700 text-xs sm:text-sm mt-1">{description}</p>
          </div>
      </div>
  );
  const isDemoCase = caseData?.case_number?.startsWith('DEMO-');
  
  // NEW LOGIC: Enable button if they have uploaded a document, media, OR logged an activity
  const isReadyToComplete = documents.length > 0 || media.length > 0 || activity.length > 0;

  return (
    <div className="p-4 pl-1 pr-1 sm:p-6 lg:p-8 max-w-7xl mx-auto bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="flex justify-between items-center mb-8 pb-4 border-b border-gray-200 print:hidden">
        <Link
          to={roleName =="client" ? "/client/cases/pi" : "/cases/pi"}
          className="flex items-center space-x-1 text-gray-600 hover:text-gray-800"
        >
          <BiArrowBack className="w-5 h-5" />
          <span className="text-sm font-medium">Back to Cases</span>
        </Link>

        <div className="flex space-x-3">

          {/* Updated button to call the new PDF function */}
          <button
            onClick={handleDownloadPdf}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 flex items-center space-x-1"
          >
            <BiDownload className="w-4 h-4" />
            <span>Download Intake Form PDF</span>
          </button>

          {canEdit && !caseData.case_number?.startsWith('DEMO-') && (
            <Link
              to={`/cases/pi/edit/${caseData.id}`}
              className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 flex items-center justify-center space-x-1"
            >
              <BiEdit className="w-4 h-4" />
              <span>Edit Case</span>
            </Link>
          )}
        </div>
      </div>

      {/* Printable Content - The ref points here */}
      <div
        ref={printRef}
        className="p-4 md:p-6 lg:p-8 bg-white shadow-xl rounded-lg border border-gray-200"
      >
        <header className="mb-6 pb-4 border-b-4 border-black text-center">
          <h1 className="text-3xl font-extrabold text-black uppercase">
            Private Investigation Case Intake
          </h1>
          <p className="text-xl text-gray-800 mt-1 font-semibold">
            {caseData.case_name}
          </p>
          <p className="text-md text-gray-600 mt-2">
            Case No:{' '}
            <span className="font-mono font-bold">{caseData.case_number}</span> |
            Status: <span className="font-bold">{caseData.status}</span>
          </p>
        </header>

        
        {/* Client Info */}
        <DetailBox
          title="Client/Billing Contact Details"
          icon={<BiUser className="w-5 h-5" />}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2">
            <DetailItem label="Client / Company" value={client_info.is_agency ? client_info.agency : client_info.full_name} />
            <DetailItem label="Contact Person" value={client_info.contact_person} />
            <DetailItem label="Phone Number" value={client_info.phone_number} />
            <DetailItem label="Email Address" value={client_info.email_address} />
            <DetailItem
              label="Preferred Contact"
              value={client_info.preferred_contact_method}
            />
            <div className="md:col-span-2">
              <DetailItem label="Billing Address" value={client_info.billing_address} />
            </div>
          </div>
        </DetailBox>

        {/* Case Info */}
        <DetailBox
          title="Case & Assignment Information"
          icon={<BiBriefcase className="w-5 h-5" />}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2">
            <DetailItem label="Client" value={caseData.client_full_name} />
            <DetailItem
              label="Client Reference #"
              value={caseData.client_reference_number}
            />
            {/* Using the pre-processed field from the backend */}
            <DetailItem label="Assigned Agents" value={assignedAgentsDisplay} />
            <DetailItem label="Case Type" value={caseData.case_type} />
            <DetailItem label="Field Work Status" value={caseData.fieldwork_status} />
            <DetailItem
              label="Date Created"
              value={
                caseData.created_at
                  ? new Date(caseData.created_at).toLocaleDateString()
                  : 'N/A'
              }
            />
            <DetailItem
              label="Due Date"
              value={
                caseData.due_date
                  ? new Date(caseData.due_date).toLocaleDateString()
                  : 'N/A'
              }
            />
          </div>
        </DetailBox>

        {/* Investigation Objectives */}
        <DetailBox
          title="Investigation Objectives & Scope"
          icon={<BiBullseye className="w-5 h-5" />}
        >
          <DetailItem label="Objectives" value={caseData.objectives} />
          <DetailItem label="Known Locations" value={caseData.known_locations} />
          <DetailItem
            label="Relevant Dates/Times"
            value={caseData.relevant_dates}
          />
        </DetailBox>


        {/* Subject Info */}
        <DetailBox
          title="Investigation Subject(s) & Files"
          icon={<BiUser className="w-5 h-5" />}
        >
          {subjects.length > 0 ? (
            <div className="space-y-6">
              {subjects.map((subject, index) => (
                <div
                  key={subject.id || index}
                  className="p-4 border border-gray-200 rounded-md"
                >
                  <h4 className="font-extrabold text-md text-black mb-2 border-b pb-1">
                    Subject #{index + 1}: {subject.full_name} ({subject.status})
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
                    <DetailItem label="Aliases/AKA" value={subject.aliases} />
                    <DetailItem label="Last Seen" value={subject.last_seen} />
                    <DetailItem
                      label="Phone/Email"
                      value={
                        `${subject.phone || ''}` +
                        (subject.phone && subject.email ? ' / ' : '') +
                        `${subject.email || ''}`
                      }
                    />
                    <DetailItem
                      label="Vehicle"
                      value={subject.vehicle_description}
                    />
                  </div>
                  <DetailItem
                    label="Description"
                    value={subject.physical_description}
                  />
                  <DetailItem label="Notes" value={subject.notes} />

                  {/* Subject Files - Uses the data processed by the backend */}
                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <h5 className="font-bold text-sm text-gray-700 mb-2">
                      Attached Files:
                    </h5>
                    {/* NOTE: File list items are now hidden from the PDF, but their titles remain for documentation */}
                    {subject.subject_attachments &&
                    subject.subject_attachments.length > 0 ? (
                      <div className="space-y-1">
                        {subject.subject_attachments.map((file) => (
                          <div key={file.id} className="text-gray-700 text-xs p-1">
                            - {file.title || 'Untitled File'} ({file.file_name})
                            <SubjectFileItem key={file.id} file={file} />
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-500 text-xs italic">
                        No specific subject files attached.
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 italic">
              No investigation subject information found.
            </p>
          )}
        </DetailBox>

        {/* Footer */}
        <div className="mt-10 pt-4 border-t border-gray-400 text-xs text-gray-500 text-right">
          <p>
            Document generated on: {new Date().toLocaleDateString()} | System ID:{' '}
            {caseData.id} | PI Case
          </p>
        </div>
      </div>

      {/* Non-Printable Sections */}
      {/* <FieldUpdateSection
        caseId={id}
        caseName={caseData.case_name}
        subjects={subjects}
        canUpload={canUpload}
      /> */}
      {isDemoCase && <OnboardingGuide step="2" title="Log an Activity Update" description="Add a field update or log your investigative activities below." />}
      <FieldUpdateSection
        caseId={id}
        caseName={caseData.case_name}
        subjects={subjects}
        canUpload={canUpload}
      />

      {planType == 'elite' && (
        <CaseMessaging caseId={id} isPiCase={true} />
      )}

      {planType == 'elite' && (
        
        <CaseMessaging caseId={id} isPiCase={true} />
      )}

      {/* Files + Activity (Hidden from PDF) */}
      <div className="mt-8 pt-8 border-t border-gray-200 print:hidden">
        {/* <h2 className="text-2xl font-extrabold text-gray-900 mb-5 flex items-center space-x-2">
          <BiFile className="w-6 h-6" /> <span>Case Files & Activity</span>
        </h2> */}
        <h2 className="text-2xl font-extrabold text-gray-900 mb-5 flex items-center space-x-2 flex-wrap gap-2">
          <div className="flex items-center space-x-2">
              <BiFile className="w-6 h-6" /> <span>Case Files & Activity</span>
          </div>
          {/* Step 1: Document Upload */}
          {isDemoCase && (
              <span className="bg-indigo-600 text-white px-3 py-1 rounded-full text-xs font-bold animate-pulse shadow-sm">
                  Step 1: Upload a Document Here
              </span>
          )}
        </h2>

        {canUpload && (
          // <DocumentUpload caseId={id} onUploadSuccess={handleUploadSuccess} />
          <DocumentUpload caseId={id} isPiCase={true} onUploadSuccess={handleUploadSuccess} />
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
          {/* Documents */}
          <div className="bg-white p-5 rounded-lg shadow-md border border-gray-100">
            <h3 className="font-semibold text-lg text-gray-800 mb-3">
              Documents ({documents.length})
            </h3>
            <ul className="space-y-2">
              {documents.length > 0 ? (
                documents.map((doc) => (
                  <li
                    key={doc.id}
                    className="flex justify-between items-center p-3 bg-gray-50 rounded-md border border-gray-200"
                  >
                    <span className="font-medium text-gray-700 text-sm">
                      {doc.file_name}
                    </span>
                    <button
                      onClick={() =>
                        handleDownload(doc.id, doc.file_name, 'documents')
                      }
                      className="text-blue-600 hover:text-blue-900 font-semibold text-xs flex items-center space-x-1"
                    >
                      <BiDownload className="w-4 h-4" />
                      <span>Download</span>
                    </button>
                  </li>
                ))
              ) : (
                <p className="text-gray-500 text-sm">
                  No official documents uploaded yet.
                </p>
              )}
            </ul>
          </div>

          {/* Media */}
          <div className="bg-white p-5 rounded-lg shadow-md border border-gray-100">
            <h3 className="font-semibold text-lg text-gray-800 mb-3">
              Media (Photos/Videos) ({media.length})
            </h3>
            <ul className="space-y-2">
              {media.length > 0 ? (
                media.map((mediaItem) => (
                  <li
                    key={mediaItem.id}
                    className="flex justify-between items-center p-3 bg-gray-50 rounded-md border border-gray-200"
                  >
                    <span className="font-medium text-gray-700 text-sm">
                      {mediaItem.file_name}
                    </span>
                    <button
                      onClick={() =>
                        handleDownload(mediaItem.id, mediaItem.file_name, 'media')
                      }
                      className="text-blue-600 hover:text-blue-900 font-semibold text-xs flex items-center space-x-1"
                    >
                      <BiDownload className="w-4 h-4" />
                      <span>Download</span>
                    </button>
                  </li>
                ))
              ) : (
                <p className="text-gray-500 text-sm">
                  No media files uploaded yet.
                </p>
              )}
            </ul>
          </div>

          {/* Activity Log */}
          <div className="bg-white p-5 rounded-lg shadow-md border border-gray-100">
            <h3 className="font-semibold text-lg text-gray-800 mb-3 flex items-center space-x-1">
              <BiTime className="w-5 h-5" /> <span>Activity Log</span>
            </h3>
            <ul className="space-y-3 text-sm">
              {activity.length > 0 ? (
                activity.map((act) => (
                  <li
                    key={act.id}
                    className="border-l-4 border-blue-600 pl-3 text-gray-700"
                  >
                    <p className="font-semibold">{act.description}</p>
                    <p className="text-xs text-gray-500">
                      {new Date(act.created_at).toLocaleString()}
                    </p>
                  </li>
                ))
              ) : (
                <p className="text-gray-500 text-sm">
                  No recent case activity recorded.
                </p>
              )}
            </ul>
          </div>
          <div className="md:col-span-3 flex justify-end mt-4">
            {/* Complete Onboarding Button */}
            {isDemoCase && (
                <div className="flex items-center space-x-2 hidden sm:flex">
                    <span className="bg-indigo-100 text-indigo-800 text-xs font-bold px-3 py-1 rounded-full border border-indigo-200">
                        Step 3: Finish!
                    </span>
                    <button 
                        onClick={() => {
                            trackOnboardingEvent('workflow_reviewed');
                            alert("🎉 Workflow reviewed! Your trial activation is complete.");
                            if (roleName === 'admin' || roleName === 'super_admin') navigate('/dashboard/admin');
                        }} 
                        disabled={!isReadyToComplete}
                        title={!isReadyToComplete ? "Please upload a document or log an activity to complete onboarding" : "Complete Onboarding"}
                        className={`px-4 py-2 text-sm font-medium text-white rounded-md flex items-center space-x-1 transition-colors ${
                            !isReadyToComplete ? 'bg-gray-400 cursor-not-allowed opacity-75' : 'bg-indigo-600 hover:bg-indigo-700 shadow-md'
                        }`}
                    >
                        <span>✅ Complete Onboarding</span>
                  </button>
                </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CaseDetailsPi;